import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { YStack, XStack, Text, Spinner } from "tamagui";
import { RiArrowRightSLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import SectionLabel from "./ui/SectionLabel";
import ListContainer from "./ui/ListContainer";
import { listPaymentRecords, orderStatusLabel, type PaymentRecordItem } from "../API/handleAccountHistoryAPI";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";
import { useSkinV2Page, v2 } from "@screen/Home/pages/GasShop/skin";

const PREVIEW_SIZE = 5;

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** 帳號頁底部：最近付費紀錄預覽（前 5 筆），點「查看全部」進完整列表。 */
export const RecentPayRecords = () => {
  const skinV2 = useSkinV2Page();
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();

  const [items, setItems] = useState<PaymentRecordItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cusJwtToken) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    listPaymentRecords({ jwtToken: cusJwtToken, params: { page: 1, limit: PREVIEW_SIZE } })
      .then((r) => {
        if (!active) return;
        if (r.state === "success") {
          setItems(r.data?.items ?? []);
          setTotal(r.data?.total ?? 0);
        } else {
          setItems([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken]);

  return (
    <YStack mt="$3">
      <SectionLabel
        title="付費紀錄"
        right={
          total > 0 ? (
            <XStack ai="center" gap="$0.5" cursor="pointer" onPress={() => navigate("/account/payRecord")}>
              <Text fontSize={12} fontWeight="600" color={colors.primary}>
                查看全部
              </Text>
              <RiArrowRightSLine size={14} color={colors.primary} />
            </XStack>
          ) : undefined
        }
      />

      {loading ? (
        <YStack ai="center" jc="center" py="$6">
          <Spinner color={colors.primary} />
        </YStack>
      ) : items.length === 0 ? (
        <ListContainer>
          <YStack ai="center" jc="center" py="$6">
            <Text fontSize={13} color={colors.sub}>
              目前沒有付費紀錄
            </Text>
          </YStack>
        </ListContainer>
      ) : (
        <ListContainer>
          {items.map((it, i) => (
            <XStack
              key={`${it.platform_order_id}-${i}`}
              ai="center"
              gap="$3"
              px="$3.5"
              py="$3"
              bbw={i === items.length - 1 ? 0 : 1}
              bbc={skinV2 ? v2.divider : colors.borderLight}
              cursor="pointer"
              onPress={() => navigate(`/account/payRecord/${it.platform_order_id}`, { state: { item: it, from: "/account" } })}>
              {/* V2：日期當標題（列表本來就是時間序），帳戶名不再重複出現在每一列；
                  瓦斯行與付款方式退成灰字；金額是這一列唯一的藍色；狀態只在「未完成」時才顯示，
                  否則整份清單右側都掛著一模一樣的「訂單已完成」，等於沒有資訊。 */}
              <YStack f={1} miw={0}>
                <Text fontSize={14} fontWeight="700" color={skinV2 ? v2.title : colors.text} numberOfLines={1}>
                  {skinV2 ? fmtDate(it.paid_at ?? it.created_at) : it.display_title}
                </Text>
                <Text fontSize={skinV2 ? 12 : 11} color={skinV2 ? v2.sub : colors.hint} mt="$0.5" numberOfLines={1}>
                  {skinV2 ? it.display_subtitle || it.display_title : `${fmtDate(it.paid_at ?? it.created_at)}${it.display_subtitle ? ` · ${it.display_subtitle}` : ""}`}
                </Text>
              </YStack>
              <YStack ai="flex-end" flexShrink={0}>
                {!isOrderAmountHidden(it.hide_amount_on_pending, it.order_status) && (
                  <Text
                    fontSize={skinV2 ? 16 : 15}
                    fontWeight="800"
                    color={skinV2 ? colors.primary : colors.text}
                    style={{ fontVariantNumeric: "tabular-nums" }}>
                    ${(it.amount ?? 0).toLocaleString()}
                  </Text>
                )}
                {(!skinV2 || it.order_status !== "completed") && (
                  <Text fontSize={10} fontWeight="600" color={colors.sub} mt="$0.5">
                    {orderStatusLabel(it.order_status)}
                  </Text>
                )}
              </YStack>
              <RiArrowRightSLine size={14} color={colors.hint} />
            </XStack>
          ))}
        </ListContainer>
      )}
    </YStack>
  );
};

export default RecentPayRecords;
